import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";
import axios from 'axios';
import Loader from '../../components/Global/Loader';
import UniversityFields from '../../components/Admin/Calculator/UniversityFields';

function Calculator() {
  const [formValues, setFormValues] = useState({
    country: 'UK',
    programme: '',
    city: '',
    university: '',
    universityId: '',
    major: '',
    duration: '',
    applicationFee: '',
    yearlyFee: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);


  const [countries, setCountries] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [cities, setCities] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [majors, setMajors] = useState([]);

  useEffect(() => {
    fetchFilterData();

    const id = queryParams.get("id");

    if (id) {
      setIsEditing(true);
      fetchSingleCalculator(id);
    }
  }, []);


   // Validation function to check if all fields are filled
   const isFormValid = () => {
    return (
      formValues.country &&
      formValues.programme &&
      formValues.city &&
      formValues.university &&
      formValues.major &&
      formValues.duration &&
      formValues.applicationFee &&
      formValues.yearlyFee
    );
  };


  // Fetch data of single calculator
  const fetchSingleCalculator = async (id) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/calculator/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setFormValues(response.data.data);
    } catch (error) {
      // console.error("Error fetching calculator data:", error);
    }
  };

  // Fetch filter data based on params
  const fetchFilterData = async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/university/filter${queryString ? `?${queryString}` : ''}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      // console.log('API Response:', response.data);

      if (!params.country) {
        // Initial load - set countries only
        setCountries(response.data.data.countries || []);
      } else if (!params.program) {
        // Country selected - set programs (though we're using fixed values)
        setPrograms(['Bachelors', 'Masters']);
      } else if (!params.city) {
        // Program selected - set cities
        setCities(response.data.data.cities || []);
      } else if (!params.universityName) {
        // City selected - set universities
        setUniversities(response.data.data.universities || []);
      } else {
        // University selected - set majors
        setMajors(response.data.data.majors || []);
      }
    } catch (error) {
      // console.error("Error fetching filter data:", error);
    }
  };


  // Save the data
  const saveData = async () => {
    try {
      setIsLoading(true);
      const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/calculator/new`, formValues, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response?.data?.success)
        setIsSubmitted(true);

    } catch (error) {
      // console.error("Error saving data:", error);
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };


  const updateData = async (id) => {
    try {
      setIsLoading(true);
      const response = await axios.patch(`${process.env.REACT_APP_BASE_URL}/calculator/${id}/update`, formValues, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response?.data?.success)
        setIsSubmitted(true);

    } catch (error) {
      // console.error("Error saving data:", error);
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form field changes
  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));

    switch (name) {
      case 'country':
        // Reset dependent fields
        setFormValues((prev) => ({
          ...prev,
          programme: '',
          city: '',
          university: '',
          major: '',
        }));
        setPrograms(['Bachelors', 'Masters']); // Set fixed programme options
        fetchFilterData({ country: value });
        break;

      case 'programme':
        setFormValues((prev) => ({
          ...prev,
          city: '',
          university: '',
          major: '',
        }));
        if (value && formValues.country) {
          fetchFilterData({
            country: formValues.country,
            program: value,
          });
        }
        break;

      case 'city':
        setFormValues((prev) => ({
          ...prev,
          university: '',
          major: '',
        }));
        if (value) {
          fetchFilterData({
            country: formValues.country,
            program: formValues.programme,
            city: value,
          });
        }
        break;

      case 'university':
        setFormValues((prev) => ({
          ...prev,
          major: '',
        }));
        if (value) {
          fetchFilterData({
            country: formValues.country,
            program: formValues.programme,
            city: formValues.city,
            universityName: value,
          });
        }
        break;

      default:
        break;
    }
  };

  // Handle duration field to accept only numbers
  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    if (/^\d*$/.test(value)) {
      setFormValues((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Get currency symbol based on country
  const getCurrencySymbol = () => {
    if (formValues.country === 'UK') {
      return '£';
    } else if (formValues.country === 'USA') {
      return '$';
    }
    return '';
  };

  useGSAP(() => {
    const fields = document.querySelectorAll(".relative");

    fields.forEach((field) => {
      const input = field.querySelector("input, select");
      const label = field.querySelector("label");
      const pLabel = field.querySelector("p"); // Optional error message element

      if (!input || !label) {
        // console.warn("Missing input or label in field:", field);
        return;
      }

      // INITIAL CHECK: If the input is already filled, animate the label to the top.
      if (input.value !== "") {
        gsap.set(label, { top: -10, fontSize: "0.70rem", color: "#6b7280" });
        gsap.set(input, { borderColor: "#6b7280" });
        if (pLabel) {
          pLabel.textContent = `Please enter your ${label.textContent.toLowerCase()}`;
          pLabel.classList.remove("text-red-500");
          pLabel.classList.add("text-gray-500");
        }
      }

      // Focus event listener
      input.addEventListener("focus", () => {
        gsap.to(label, { top: -10, fontSize: "0.70rem", color: "#22c55e", duration: 0.25 });
        gsap.to(input, { borderColor: "#22c55e", duration: 0.25 });
        if (pLabel) {
          pLabel.classList.remove("text-red-500");
          pLabel.classList.add("text-gray-500");
        }
      });

      // Input event listener
      input.addEventListener("input", () => {
        if (input.value !== "") {
          gsap.to(label, { color: "#22c55e", duration: 0.25 });
          gsap.to(input, { borderColor: "#22c55e", duration: 0.25 });
          if (pLabel) {
            pLabel.classList.remove("text-red-500");
            pLabel.classList.add("text-gray-500");
          }
        }
      });

      // Blur event listener
      input.addEventListener("blur", () => {
        if (input.value === "") {
          gsap.to(label, { top: 10, fontSize: "1rem", color: "#6b7280", duration: 0.25 });
          gsap.to(input, { borderColor: "#ef4444", duration: 0.25 });
          input.classList.add("error-border");
          if (pLabel) {
            pLabel.textContent = `${label.textContent} Required *`;
            pLabel.classList.add("text-red-500");
          }
        } else {
          gsap.to(label, { top: -10, fontSize: "0.70rem", color: "#6b7280", duration: 0.25 });
          gsap.to(input, { borderColor: "#6b7280", duration: 0.25 });
          input.classList.remove("error-border");
          if (pLabel) {
            pLabel.textContent = `Please enter your ${label.textContent.toLowerCase()}`;
            pLabel.classList.remove("text-red-500");
            pLabel.classList.add("text-gray-500");
          }
        }
      });
    });
  }, [formValues, isEditing]);

  const handleSubmit = () => {
    const id = queryParams.get("id");
    if (id) {
      updateData(id);
    } else {
      saveData();
    }
  };

  return (
    <>
      <div id="main-content" className="h-screen relative pl-36 mt-16 w-full p-8 bg-[#f4f8fb]">
        {isLoading && <Loader />}
        {
          isSubmitted && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
              <div className="bg-white w-full max-w-md mx-auto rounded-lg shadow-lg p-6">
                {/* Modal Content */}
                <h2 className="text-lg font-semibold text-gray-800 mb-4">
                  Success!
                </h2>
                {isEditing ?
                  <h4 className="text-sm text-gray-600 mb-6">
                    Calculator data has been updated successfully.
                  </h4> :
                  <h4 className="text-sm text-gray-600 mb-6">
                    Calculator data has been saved successfully.
                  </h4>
                }
                <div className="flex justify-end space-x-4">
                  {/* Proceed Button */}
                  <button
                    onClick={() => navigate("/admin/calculator")}
                    className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 focus:outline-none"
                  >
                    Okay
                  </button>
                </div>
              </div>
            </div>
          )
        }

        {
          error && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
              <div className="bg-white w-full max-w-md mx-auto rounded-lg shadow-lg p-6">
                {/* Modal Content */}
                <h2 className="text-lg font-semibold text-gray-800 mb-4">
                  Something went wrong!
                </h2>
                <h4 className="text-sm text-gray-600 mb-6">
                  {error?.response?.data?.message || `We encountered an issue while processing your request. Please try again later. If the problem persists, contact support.`}
                </h4>
                <div className="flex justify-end space-x-4">
                  {/* Proceed Button */}
                  <button
                    onClick={() => setError(null)}
                    className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 focus:outline-none"
                  >
                    Okay
                  </button>
                </div>
              </div>
            </div>
          )
        }
        <div className="flex flex-wrap space-x-6">
          <div className="px-10 py-6 bg-white rounded shadow-lg w-full">
            <h2 className="text-gray-800 text-3xl font-semibold mt-6 mb-4">Calculator</h2>
            <hr className="border-t border-gray-300 mb-10" />

            <UniversityFields
              formData={formValues}
              handleChange={handleChange}
              countries={countries}
              cities={cities}
              universities={universities}
              majors={majors}
              setFormData={setFormValues}
            />

            {/* Third Row of Fields */}
            <h3 className='mb-4 font-semibold'>Fee Details: </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              {/* Duration */}
              <div className="relative">
                <label htmlFor="duration" className="absolute text-sm text-gray-500 top-2 left-3 bg-white px-1">
                  Duration
                </label>
                <input
                  id="duration"
                  name="duration"
                  value={formValues.duration}
                  onChange={handleNumberChange}
                  type="text"
                  className="w-full p-2 border rounded"
                />
                <p className="text-gray-500 text-xs mt-1">Please enter duration in years</p>
              </div>

              {/* Application Fee */}
              <div className="relative">
                <label htmlFor="applicationFee" className="absolute z-10 text-sm text-gray-500 top-2 left-3 bg-white px-1">
                  Application Fee
                </label>
                <div className="flex items-center">
                  <span className="absolute left-3 font-semibold">{getCurrencySymbol()}</span>
                  <input
                    id="applicationFee"
                    name="applicationFee"
                    value={formValues.applicationFee}
                    onChange={handleNumberChange}
                    type="text"
                    className="w-full pl-6 p-2 border rounded"
                  />
                </div>
                <p className="text-gray-500 text-xs mt-1">Please enter application fee</p>
              </div>

              {/* Yearly Fee */}
              <div className="relative">
                <label htmlFor="yearlyFee" className="absolute z-10 text-sm text-gray-500 top-2 left-3 bg-white px-1">
                  Yearly Fee
                </label>
                <div className="flex items-center">
                  <span className="absolute left-3 font-semibold">{getCurrencySymbol()}</span>
                  <input
                    id="yearlyFee"
                    name="yearlyFee"
                    value={formValues.yearlyFee}
                    onChange={handleNumberChange}
                    type="text"
                    className="w-full pl-6 p-2 border rounded"
                  />
                </div>
                <p className="text-gray-500 text-xs mt-1">Please enter yearly fee</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end mt-10 mr-5">
              <button
                onClick={handleSubmit}
                disabled={!isFormValid()}
                className="bg-white text-blue-500 border border-blue-500 px-4 py-2 rounded hover:bg-blue-500 hover:text-white disabled:opacity-50 disabled:hover:bg-white disabled:hover:text-blue-500"
              >
                Save & Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Calculator;